import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schemaJson?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schemaJson,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, propName = 'name') => {
      let el = document.querySelector(`meta[${propName}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(propName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', ogType, 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');
    setMeta('twitter:card', 'summary_large_image', 'property');
    setMeta('twitter:title', title, 'property');
    setMeta('twitter:description', description, 'property');
  }, [title, description, keywords, canonicalUrl, ogType, ogImage]);

  useEffect(() => {
    if (schemaJson) {
      const scriptId = 'schema-jsonld';
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schemaJson, null, 2);
      return () => {
        const s = document.getElementById(scriptId);
        if (s) s.remove();
      };
    }
  }, [schemaJson]);

  return null;
}